import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import {
  ArrowLeft,
  Clock,
  Film,
  LoaderCircle,
  MoreVertical,
  Trash2,
} from 'lucide-react'
import { motion } from 'motion/react'

import { Button } from '@/components/ui/button'
import {
  getTemplate,
  UNKNOWN_TEMPLATE_ICON,
} from '@/features/creation/templates'
import {
  type ProjectListItem,
  type ProjectStatus,
  useDeleteProjectMutation,
  useProjectsListQuery,
} from '@/features/projects/api'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/projects')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' })
    }
  },
  component: ProjectsPage,
})

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
}

const fadeUp = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] as const },
  },
}

const STATUS_CONFIG: Record<
  ProjectStatus,
  { label: string; className: string }
> = {
  draft: { label: 'Draft', className: 'bg-yellow-100 text-yellow-800' },
  rendering: {
    label: 'Rendering',
    className: 'bg-blue-100 text-blue-700 animate-pulse',
  },
  complete: { label: 'Complete', className: 'bg-emerald-100 text-emerald-800' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-800' },
}

function formatRelative(isoDate: string): string {
  const date = new Date(isoDate)
  const diffMs = Date.now() - date.getTime()
  const minutes = Math.round(diffMs / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days}d ago`
  return date.toLocaleDateString()
}

function StatusPill({ status }: { status: ProjectStatus }) {
  const config = STATUS_CONFIG[status]
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        config.className,
      )}
    >
      {config.label}
    </span>
  )
}

function ProjectCard({
  project,
  onDelete,
  isDeleting,
}: {
  project: ProjectListItem
  onDelete: (id: string) => void
  isDeleting: boolean
}) {
  const templateDef = getTemplate(project.template)
  const TemplateIcon = templateDef?.icon ?? UNKNOWN_TEMPLATE_ICON
  const templateLabel = templateDef?.label ?? project.template

  return (
    <div className="group overflow-hidden rounded-xl border bg-card shadow-card transition-shadow duration-200 hover:shadow-card-hover">
      <Link
        to="/create"
        search={{ projectId: project.id }}
        className="relative flex aspect-video items-center justify-center bg-zinc-900"
        aria-label={`Open ${project.title}`}
      >
        {project.thumbnail_url ? (
          <img
            src={project.thumbnail_url}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <Film className="size-10 text-zinc-600" strokeWidth={1.2} />
        )}
        <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-xs font-medium text-white backdrop-blur-sm">
          <TemplateIcon className="size-3" />
          {templateLabel}
        </span>
      </Link>

      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-2 text-sm font-semibold leading-snug">
            {project.title}
          </h3>
          <StatusPill status={project.status} />
        </div>

        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="size-3" />
            {formatRelative(project.updated_at)}
          </span>

          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label={`More actions for ${project.title}`}
            >
              <MoreVertical className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-muted-foreground hover:text-destructive"
              aria-label={`Delete ${project.title}`}
              disabled={isDeleting}
              onClick={() => {
                if (confirm(`Delete "${project.title}"?`)) onDelete(project.id)
              }}
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
      <div className="flex size-16 items-center justify-center rounded-2xl bg-muted">
        <Film className="size-8 text-muted-foreground" strokeWidth={1.4} />
      </div>
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">No projects yet</h2>
        <p className="text-sm text-muted-foreground">
          Create your first video to see it here.
        </p>
      </div>
      <Button asChild>
        <Link to="/create">Create a video</Link>
      </Button>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-24 text-muted-foreground">
      <LoaderCircle className="size-6 animate-spin" />
      <p className="text-sm">Loading your projects...</p>
    </div>
  )
}

function ErrorState({ error }: { error: unknown }) {
  const message =
    error instanceof Error ? error.message : 'Something went wrong'
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">Could not load projects</h2>
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
    </div>
  )
}

function ProjectsPage() {
  const { data, isLoading, error } = useProjectsListQuery()
  const deleteMutation = useDeleteProjectMutation()

  return (
    <div className="relative min-h-screen px-4 pb-16 pt-6 sm:px-6 lg:px-8">
      <motion.header
        className="mx-auto mb-8 flex max-w-5xl items-center gap-3"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <Button variant="ghost" size="icon" className="shrink-0" asChild>
          <Link to="/dashboard" aria-label="Back to dashboard">
            <ArrowLeft className="size-5" />
          </Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">My Projects</h1>
        {data && (
          <span className="ml-1 text-sm text-muted-foreground">
            ({data.total})
          </span>
        )}
      </motion.header>

      <div className="mx-auto max-w-5xl">
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState error={error} />
        ) : !data || data.items.length === 0 ? (
          <EmptyState />
        ) : (
          <motion.div
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
            variants={stagger}
            initial="hidden"
            animate="visible"
          >
            {data.items.map((project) => (
              <motion.div key={project.id} variants={fadeUp}>
                <ProjectCard
                  project={project}
                  onDelete={(id) => deleteMutation.mutate(id)}
                  isDeleting={
                    deleteMutation.isPending &&
                    deleteMutation.variables === project.id
                  }
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  )
}
